import React, { useEffect, useMemo, useState } from 'react';

const escapeHtml = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderInline = (value) => {
  let rendered = escapeHtml(value);

  rendered = rendered.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  rendered = rendered.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  rendered = rendered.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  return rendered;
};

const isBlockBoundary = (line) =>
  line === '' ||
  line === '---' ||
  line.startsWith('# ') ||
  line.startsWith('## ') ||
  line.startsWith('### ') ||
  line.startsWith('- ') ||
  line.startsWith('<aside>') ||
  line.startsWith('</aside>');

const renderParagraph = (lines) => {
  const content = lines.join(' ').replace(/\s+/g, ' ').trim();
  return content ? `<p>${renderInline(content)}</p>` : '';
};

const markdownToHtml = (lines) => {
  const html = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();

    if (!line) {
      continue;
    }

    if (line.startsWith('<aside>')) {
      const asideLines = [];
      let cursor = index + 1;

      while (cursor < lines.length && !lines[cursor].trim().startsWith('</aside>')) {
        const asideLine = lines[cursor].trim();
        if (asideLine) {
          asideLines.push(asideLine);
        }
        cursor += 1;
      }

      const icon = asideLines[0] === '💡' ? asideLines.shift() : null;
      const body = asideLines.join(' ').replace(/\s+/g, ' ').trim();

      html.push(
        `<aside class="Cgs-callout">${icon ? `<div class="Cgs-calloutIcon">${icon}</div>` : ''}<div>${body ? `<p>${renderInline(body)}</p>` : ''}</div></aside>`
      );
      index = cursor;
      continue;
    }

    if (line === '---') {
      html.push('<hr />');
      continue;
    }

    if (line.startsWith('### ')) {
      html.push(`<h3>${renderInline(line.slice(4))}</h3>`);
      continue;
    }

    if (line.startsWith('## ')) {
      html.push(`<h2>${renderInline(line.slice(3))}</h2>`);
      continue;
    }

    if (line.startsWith('# ')) {
      html.push(`<h1>${renderInline(line.slice(2))}</h1>`);
      continue;
    }

    if (line.startsWith('- ')) {
      const items = [];
      let cursor = index;

      while (cursor < lines.length && lines[cursor].trim().startsWith('- ')) {
        items.push(`<li>${renderInline(lines[cursor].trim().slice(2))}</li>`);
        cursor += 1;
      }

      html.push(`<ul>${items.join('')}</ul>`);
      index = cursor - 1;
      continue;
    }

    const paragraphLines = [line];
    let cursor = index + 1;

    while (cursor < lines.length && !isBlockBoundary(lines[cursor].trim())) {
      paragraphLines.push(lines[cursor].trim());
      cursor += 1;
    }

    html.push(renderParagraph(paragraphLines));
    index = cursor - 1;
  }

  return html.join('');
};

const parseMarkdownDocument = (markdown) => {
  const lines = markdown.replace(/\r/g, '').split('\n');
  let cursor = 0;

  while (cursor < lines.length && !lines[cursor].trim()) {
    cursor += 1;
  }

  let title = 'Conditions Générales de Service';
  if (cursor < lines.length && lines[cursor].trim().startsWith('# ')) {
    title = lines[cursor].trim().slice(2);
    cursor += 1;
  }

  while (cursor < lines.length && !lines[cursor].trim()) {
    cursor += 1;
  }

  let meta = '';
  if (cursor < lines.length) {
    const currentLine = lines[cursor].trim();
    if (currentLine.startsWith('*') && currentLine.endsWith('*')) {
      meta = currentLine.slice(1, -1).trim();
      cursor += 1;
    }
  }

  const bodyLines = lines.slice(cursor);

  return {
    title,
    meta,
    html: markdownToHtml(bodyLines),
  };
};

const CGS_SESSION_EMAIL_KEY_PREFIX = 'cgs-email:';

const getCGSDocumentId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('documentId') || params.get('id') || '';
};

const getCGSApiBaseUrl = () => {
  if (process.env.REACT_APP_FUNCTIONS_WIDGETS_ENDPOINT) {
    return process.env.REACT_APP_FUNCTIONS_WIDGETS_ENDPOINT;
  }

  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (isLocalHost) {
    const projectId = process.env.REACT_APP_PROJECT_ID || 'ava-app-dev-92f7f';
    return `http://127.0.0.1:5001/${projectId}/us-central1/widgets`;
  }

  return '';
};

const getCGSSessionEmailKey = (documentId) => `${CGS_SESSION_EMAIL_KEY_PREFIX}${documentId}`;

const Cgs = () => {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const documentId = useMemo(() => getCGSDocumentId(), []);
  const cgsApiBaseUrl = useMemo(() => getCGSApiBaseUrl(), []);

  const authenticateEmail = async (candidateEmail) => {
    if (!documentId || !cgsApiBaseUrl) {
      return false;
    }

    try {
      const response = await fetch(`${cgsApiBaseUrl}/cgs/authenticate?documentId=${encodeURIComponent(documentId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: candidateEmail.trim() }),
      });

      if (!response.ok) {
        return false;
      }

      const payload = await response.json();
      return payload?.response === true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!documentId) {
      setAuthError('This CGS link is invalid.');
      setIsAuthenticating(false);
      return undefined;
    }

    if (!cgsApiBaseUrl) {
      setAuthError('Unable to reach the CGS authentication service.');
      setIsAuthenticating(false);
      return undefined;
    }

    let active = true;
    const storedEmail = window.sessionStorage.getItem(getCGSSessionEmailKey(documentId));

    if (!storedEmail) {
      setIsAuthenticating(false);
      return undefined;
    }

    setEmailInput(storedEmail);

    authenticateEmail(storedEmail).then((isValid) => {
      if (!active) {
        return;
      }

      if (isValid) {
        setEmail(storedEmail);
      } else {
        window.sessionStorage.removeItem(getCGSSessionEmailKey(documentId));
        setAuthError('This email address is not valid for this document.');
      }

      setIsAuthenticating(false);
    });

    return () => {
      active = false;
    };
  }, [cgsApiBaseUrl, documentId]);

  useEffect(() => {
    if (!email) {
      return undefined;
    }

    let active = true;

    fetch('/cgs.md')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to load CGS content.');
        }

        return response.text();
      })
      .then((content) => {
        if (active) {
          setMarkdown(content);
        }
      })
      .catch(() => {
        if (active) {
          setError('Unable to load the CGS document.');
        }
      });

    return () => {
      active = false;
    };
  }, [email]);

  const { title, meta, html } = useMemo(() => parseMarkdownDocument(markdown), [markdown]);

  const handleAuthenticate = async (event) => {
    event.preventDefault();

    const normalizedEmail = emailInput.trim().toLowerCase();
    if (!normalizedEmail) {
      setAuthError('Please enter your email address.');
      return;
    }

    setIsSubmitting(true);
    setAuthError('');

    const isValid = await authenticateEmail(normalizedEmail);

    if (isValid) {
      window.sessionStorage.setItem(getCGSSessionEmailKey(documentId), normalizedEmail);
      setEmail(normalizedEmail);
    } else {
      window.sessionStorage.removeItem(getCGSSessionEmailKey(documentId));
      setAuthError('This email address is not valid for this document.');
    }

    setIsSubmitting(false);
  };

  const showDocument = !!email;

  return (
    <div className="CgsPage">
      <header className="CgsPage-topbar">
        <div className="CgsPage-topbarInner">
          <div className="CgsPage-brand">
            <span className="CgsPage-brandWordmark">ava</span>
            <span className="CgsPage-brandMeta">France SASU</span>
          </div>
          <div className="CgsPage-topbarMeta">
            <span>CGS</span>
            <span className="CgsPage-topbarDot">·</span>
            <span>{meta || 'Document contractuel'}</span>
          </div>
        </div>
      </header>

      <main className="CgsPage-main">
        <section className="CgsPage-hero">
          <div className="CgsPage-countMarker">
            <span className="CgsPage-countMarkerNum">01 / 01</span>
            <span className="CgsPage-countMarkerLabel">{showDocument ? 'Document de reference' : 'Verification prealable'}</span>
          </div>
          <h1 className="CgsPage-heroTitle">
            {showDocument ? title : 'Acces au document'}
          </h1>
          <p className="CgsPage-heroLead">
            {showDocument
              ? 'Reference contractuelle publique pour l\'utilisation du service Ava, presentee dans une mise en page editoriale plus lisible.'
              : 'Saisissez votre adresse email pour ouvrir ce document. L\'acces est reverifie a chaque rafraichissement et l\'email est conserve uniquement en session.'}
          </p>
        </section>

        {!showDocument ? (
          <section className="CgsPage-shell CgsPage-authShell">
            <>
              <div className="CgsPage-shellHeader">
                <div>
                  <div className="CgsPage-shellEyebrow">Conditions-cadres</div>
                  <h2 className="CgsPage-shellTitle">Authentification par email</h2>
                </div>
                <div className="CgsPage-shellMeta">
                  <span className="CgsPage-shellMetaLabel">Acces</span>
                  <span>Session courante</span>
                </div>
              </div>

              <div className="CgsPage-authCard">
                <p className="CgsPage-authQuote">
                  Votre adresse email doit correspondre a celle associee a cette demande de validation.
                </p>

                <form className="CgsPage-authForm" onSubmit={handleAuthenticate}>
                  <label className="CgsPage-authField">
                    <span className="CgsPage-authLabel">Adresse email</span>
                    <input
                      type="email"
                      className="CgsPage-authInput"
                      value={emailInput}
                      onChange={(event) => setEmailInput(event.target.value)}
                      placeholder="vous@entreprise.com"
                      autoComplete="email"
                      disabled={isAuthenticating || isSubmitting || !documentId || !cgsApiBaseUrl}
                    />
                  </label>

                  {authError ? <div className="error CgsPage-authError">{authError}</div> : null}
                  {isAuthenticating ? <p className="CgsPage-status">Checking your previous session...</p> : null}

                  <div className="CgsPage-authActions">
                    <button
                      type="submit"
                      className="CgsPage-authButton"
                      disabled={isAuthenticating || isSubmitting || !documentId || !cgsApiBaseUrl}
                    >
                      {isSubmitting ? 'Checking...' : 'Open document'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          </section>
        ) : (
          <section className="CgsPage-shell">
            {error ? <div className="error">{error}</div> : null}
            {!error && !markdown ? <p className="CgsPage-status">Loading document...</p> : null}
            {!error && markdown ? (
              <>
                <div className="CgsPage-shellHeader">
                  <div>
                    <div className="CgsPage-shellEyebrow">Conditions-cadres</div>
                    <h2 className="CgsPage-shellTitle">{title}</h2>
                  </div>
                  {meta ? (
                    <div className="CgsPage-shellMeta">
                      <span className="CgsPage-shellMetaLabel">Version</span>
                      <span>{meta}</span>
                    </div>
                  ) : null}
                </div>
                <article className="CgsPage-content" dangerouslySetInnerHTML={{ __html: html }} />
              </>
            ) : null}
          </section>
        )}

        <footer className="CgsPage-footer">
          <div>
            <div className="CgsPage-footerWordmark">ava</div>
            <div className="CgsPage-footerText">Ava France SASU</div>
          </div>
          <div className="CgsPage-footerText CgsPage-footerTextRight">
            <div>Questions juridiques : <a href="mailto:legal@ava.me">legal@ava.me</a></div>
            <div className="CgsPage-footerMono">{meta || 'CGS'}</div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Cgs;
